import { useState, useCallback } from 'react'
import { showValidationMessage, formatApiError } from '@/lib/utils/notifications'
import { getUserDetail } from '../services/userManagementApi'
import type { UserDetail } from '../types'

/**
 * Hook for loading and managing a single user's detail data.
 * Used by UserManagementEditPage.
 */
export function useUserDetail(userId: string | number) {
  const [user, setUser] = useState<UserDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchUser = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent
    if (!silent) {
      setLoading(true)
      setError(null)
    }
    try {
      const data = await getUserDetail(userId)
      setUser(data)
      return data
    } catch (err) {
      const msg = formatApiError(err)
      setError(msg)
      if (!silent) {
        showValidationMessage(msg, 'Error')
        setUser(null)
      }
      return null
    } finally {
      if (!silent) setLoading(false)
    }
  }, [userId])

  /** Patch a subset of user fields without re-fetching (e.g. after account status toggle). */
  const patchUser = useCallback((patch: Partial<UserDetail>) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev))
  }, [])

  return {
    user,
    loading,
    error,
    fetchUser,
    patchUser,
  }
}
